import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/base/ComicCard';
import { categories, allComics } from '@/mocks/comics';
import type { Category } from '@/mocks/types';

const COMICS_PER_PAGE = 12;

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'romance');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'latest' | 'hot' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const activeCategory: Category | undefined = categories.find((c) => c.slug === activeSlug);

  const filteredComics = useMemo(() => {
    let comics = allComics.filter((c) => c.category === activeSlug);

    if (sortBy === 'hot') {
      comics = [...comics].sort((a, b) => b.views - a.views);
    } else if (sortBy === 'rating') {
      comics = [...comics].sort((a, b) => b.rating - a.rating);
    }
    // latest is default order (by updateTime)
    if (sortBy === 'latest') {
      comics = [...comics].sort((a, b) => new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime());
    }

    return comics;
  }, [activeSlug, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredComics.length / COMICS_PER_PAGE));
  const pagedComics = filteredComics.slice(
    (currentPage - 1) * COMICS_PER_PAGE,
    currentPage * COMICS_PER_PAGE
  );

  const handleCategoryChange = (newSlug: string) => {
    setActiveSlug(newSlug);
    setCurrentPage(1);
    window.history.replaceState(null, '', `/category/${newSlug}`);
  };

  // SEO JSON-LD effect
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `${activeCategory?.name || ''}韩漫 - 韩漫网站`,
      url: `https://koreanmanhwa.site/category/${activeSlug}`,
      description: `韩漫网站${activeCategory?.name || ''}分类提供高清无删减韩国漫画免费在线阅读，下拉式韩漫大全。`,
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://koreanmanhwa.site/' },
          { '@type': 'ListItem', position: 2, name: `${activeCategory?.name || ''}韩漫`, item: `https://koreanmanhwa.site/category/${activeSlug}` },
        ],
      },
    };

    const existingScript = document.querySelector('script[data-seo="category"]');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'category');
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    // Update meta
    document.title = `${activeCategory?.name || ''}韩漫_高清无删减免费在线阅读-韩漫网站`;

    return () => {
      const el = document.querySelector('script[data-seo="category"]');
      if (el) el.remove();
    };
  }, [activeSlug, activeCategory]);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-16">
        {/* Breadcrumb */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-foreground-800 font-medium">
              {activeCategory?.name || ''}韩漫
            </span>
          </nav>
        </div>

        {/* Category tabs */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-6">
          <div className="flex items-center gap-1.5 flex-wrap">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => handleCategoryChange(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-white'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200/70 hover:text-foreground-900 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} text-sm`} />
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Header */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                {activeCategory?.name}韩漫
              </h1>
              <p className="text-sm text-foreground-500 mt-1">
                共 {filteredComics.length} 部韩国漫画
              </p>
            </div>

            {/* Sort */}
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { key: 'latest', label: '最新' },
                { key: 'hot', label: '最热' },
                { key: 'rating', label: '评分' },
              ].map((item) => (
                <button
                  key={item.key}
                  onClick={() => { setSortBy(item.key as typeof sortBy); setCurrentPage(1); }}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    sortBy === item.key
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Comics grid */}
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 pb-10">
          {pagedComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
                {pagedComics.map((comic) => (
                  <div key={comic.id} className="flex justify-center">
                    <ComicCard comic={comic} />
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line" />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                        currentPage === page
                          ? 'bg-primary-500 text-white'
                          : 'bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200/70 text-foreground-600 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                <i className="ri-book-open-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500">该分类暂无漫画</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}